import { Component, inject } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ReactiveFormsModule } from '@angular/forms';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { tap } from 'rxjs';
import { emailValidator } from '../../../app.validators';

@Component({
  selector: 'forgot-password',
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    SkeletonModule,
    ButtonModule,
    InputTextModule,
  ],
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent {
  isLoadingYYCLogo: boolean = true;
  isSubmitting: boolean = false;

  authService = inject(AuthService);
  router = inject(Router);
  // Gates the autocomplete tokens emitted by the email input.
  // See RuntimeConfig.authAutocompleteEnabled for the developer-note context.
  private runtimeConfig = inject(RuntimeConfigService);
  autocompleteEnabled = this.runtimeConfig.authAutocompleteEnabled;

  // `Validators.required` is kept even though an empty email shows NO inline
  // message: it is what keeps `isButtonDisabled()` true on a blank field, so
  // the disabled "Reset Password" button is the only signal for "empty".
  // `emailValidator()` is the only one with user-facing copy ("Invalid email
  // format") - see the template.
  forgotPasswordForm = new FormGroup({
    email: new FormControl<string>('', [
      Validators.required,
      emailValidator()
    ]),
  });

  get inputtedEmail() {
    return this.forgotPasswordForm.get('email');
  }

  isButtonDisabled(): boolean {
    return this.forgotPasswordForm.invalid || this.isSubmitting;
  }

  submitForm() {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const credentials = this.forgotPasswordForm.value as {
      email: string;
    };

    this.authService
      .forgotPassword(credentials)
      .pipe(
        tap({
          finalize: () => {
            this.isSubmitting = false;
            this.router.navigate(['/auth/reset-link'], {
              state: { email: this.inputtedEmail?.value,}}
            );
          },
        })
      )
      .subscribe();
  }
}
